import React from 'react';
import { Link } from 'react-router-dom';
import { 
  StockMarketIcon, 
  InvestmentIcon, 
  TradingIcon, 
  RiskManagementIcon, 
  MarketAnalysisIcon, 
  TradingPlatformsIcon,
  PortfolioWheel
} from '../components/CustomIcons';
import InteractiveChart from '../components/InteractiveChart';

const Home = () => {
const educationalPillars = [
    {
      title: "Stock Market Fundamentals",
      description: "Learn what the stock market is, how it works, and who participates in it.",
      IconComponent: StockMarketIcon,
      color: "#3498db",
      link: "/fundamentals",
      topics: ["What is the Stock Market?", "Market Participants", "Stock Exchanges"]
    },
    {
      title: "Investment Basics",
      description: "Understand different types of investments and how to build a portfolio.",
      IconComponent: InvestmentIcon,
      color: "#27ae60",
      link: "/investment-basics",
      topics: ["Types of Investments", "Building a Portfolio", "Investment Strategies"]
    },
    {
      title: "Trading Principles",
      description: "Master the mechanics of trading and understand different order types.",
      IconComponent: TradingIcon,
      color: "#e74c3c",
      link: "/trading-principles",
      topics: ["Order Types", "Trading Mechanics", "Trading Psychology"]
    },
    {
      title: "Risk Management",
      description: "Learn how to protect your investments and manage risk effectively.",
      IconComponent: RiskManagementIcon,
      color: "#f39c12",
      link: "/risk-management",
      topics: ["Understanding Risk", "Diversification", "Position Sizing"]
    },
    {
      title: "Market Analysis",
      description: "Discover how to analyze stocks and make informed investment decisions.",
      IconComponent: MarketAnalysisIcon,
      color: "#9b59b6",
      link: "/market-analysis",
      topics: ["Fundamental Analysis", "Technical Analysis", "Economic Indicators"]
    },
    {
      title: "Trading Platforms",
      description: "Compare popular trading platforms to find the one that best fits your needs.",
      IconComponent: TradingPlatformsIcon,
      color: "#34495e",
      link: "/trading-platforms",
      topics: ["Platform Features", "Cost Comparison", "Best For Different Traders"]
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden" style={{
        backgroundImage: 'url(/hero-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        paddingTop: '120px',
        paddingBottom: '80px'
      }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="fw-bold mb-4" style={{ 
                color: '#2c3e50', 
                textShadow: '2px 2px 4px rgba(255,255,255,0.8)' 
              }}>
                Master the Stock Market
              </h1>
              <p className="lead mb-4" style={{ 
                color: '#2c3e50', 
                textShadow: '2px 2px 4px rgba(255,255,255,0.8)' 
              }}>
                Learn investing fundamentals, trading principles, and risk management 
                with our comprehensive educational platform designed for beginners.
              </p>
              <div className="mt-4">
                <Link to="/fundamentals" className="btn btn-primary-custom btn-lg me-3">
                  Start Learning
                </Link>
                <Link to="/quiz" className="btn btn-outline-custom btn-lg">
                  Test Your Knowledge
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Educational Pillars */}
      <section className="content-section">
        <div className="container">
      <div className="text-center mb-5">
            <h2>Your Learning Journey</h2>
            <p className="lead">
              Master these five essential areas to become a confident investor and explore trading platforms to find your best fit
            </p>
          </div>
          
          <div className="pillars-grid">
            {educationalPillars.map((pillar, index) => {
              const IconComponent = pillar.IconComponent;
              return (
                <Link 
                  key={index} 
                  to={pillar.link} 
                  className="pillar-card"
                >
                  <div className="text-center">
                    <div className="pillar-icon">
                      <IconComponent size={80} color={pillar.color} />
                    </div>
                    <h3 style={{color: pillar.color}}>{pillar.title}</h3>
                    <p className="mb-3">{pillar.description}</p>
                    <div className="text-start">
                      <small className="text-muted">You'll learn:</small>
                      <ul className="list-unstyled mt-2">
                        {pillar.topics.map((topic, topicIndex) => (
                          <li key={topicIndex} className="mb-1">
                            <small>• {topic}</small>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Diversified Portfolio Section */}
      <section className="content-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="text-center">
                <div className="bg-light p-4 rounded">
                  <h3 className="mb-4">📊 Diversified Portfolio Example</h3>
                  <PortfolioWheel size={280} />
                  <p className="mt-3 text-muted">
                    Learn how to build a balanced portfolio across different asset classes
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Market Demo */}
      <section className="content-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2>See Market Growth in Action</h2>
                <p className="lead">
                  Explore how investments can grow over time with our interactive market visualization
                </p>
              </div>
              <div className="bg-white p-4 rounded shadow-sm">
                <InteractiveChart />
              </div>
              <div className="text-center mt-4">
                <p className="text-muted">
                  <small>
                    * This is a simplified example for educational purposes. 
                    Real market performance varies and past performance doesn't guarantee future results.
                  </small>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="content-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="text-center mb-5">Why Choose Our Platform?</h2>
              <div className="mt-4">
                <div className="d-flex mb-4">
                  <div className="me-3">
                    <span className="badge bg-primary rounded-pill">1</span>
                  </div>
                  <div>
                    <h5>Beginner-Friendly Content</h5>
                    <p>Complex concepts explained in simple, easy-to-understand language with real-world examples.</p>
                  </div>
                </div>
                <div className="d-flex mb-4">
                  <div className="me-3">
                    <span className="badge bg-primary rounded-pill">2</span>
                  </div>
                  <div>
                    <h5>Interactive Learning</h5>
                    <p>Engage with quizzes, interactive charts, and practical exercises to reinforce your knowledge.</p>
                  </div>
                </div>
                <div className="d-flex mb-4">
                  <div className="me-3">
                    <span className="badge bg-primary rounded-pill">3</span>
                  </div>
                  <div>
                    <h5>Comprehensive Coverage</h5>
                    <p>From basic concepts to advanced strategies, we cover everything you need to know.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="content-section bg-primary text-white">
        <div className="container text-center">
          <h2>Ready to Start Your Investment Journey?</h2>
          <p className="lead mb-4">
            Join thousands of learners who have mastered the fundamentals of investing
          </p>
          <Link to="/fundamentals" className="btn btn-light btn-lg">
            Begin Learning Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
